// Workshop Recall Interface - Main Application
// Voice-led system with touch as fallback

import { useCallback, useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AnimatePresence, LayoutGroup } from 'framer-motion';
import { clsx } from 'clsx';
import { UserButton } from '@clerk/clerk-react';
import { dark } from '@clerk/themes';

import { useAppStore } from '@/store/appStore';
import { useVenturaVoice } from '@/hooks/useVenturaVoice';
import { useIdleTimer } from '@/hooks/useIdleTimer';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';

import { SearchBar } from '@/components/search/SearchBar';
import { FilterChips } from '@/components/search/FilterChips';
import { ResultsGrid } from '@/components/results/ResultsGrid';
import { ImageDetail } from '@/components/image/ImageDetail';
import { VoiceOverlay } from '@/components/voice/VoiceOverlay';
import { ShowcaseMode } from '@/components/showcase/ShowcaseMode';
import { ComparisonView } from '@/components/comparison/ComparisonView';
import { HelpButton } from '@/components/help/HelpModal';
import { SettingsButton } from '@/components/settings/SettingsModal';
import { VehicleScopeSelector } from '@/components/settings/VehicleScopeSelector';
import { MemoryView } from '@/components/memory/MemoryView';
import { useSettingsStore } from '@/store/settingsStore';

import { searchImages, getShowcaseImages, getComparison, getSimilarImages, getVehicles, isDemoMode } from '@/services/api';
import type { VoiceIntent, ComponentCategory, ConditionState, Vehicle } from '@/types';

// Check if running in demo mode
const DEMO_MODE = isDemoMode();

// React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      retry: 2,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <WorkshopInterface />
    </QueryClientProvider>
  );
}

// Component keywords for highlighting (blue)
const COMPONENT_KEYWORDS = [
  'engine', 'motor', 'block', 'heads', 'intake', 'exhaust',
  'suspension', 'shocks', 'springs', 'control arms', 'sway bar',
  'interior', 'seats', 'dashboard', 'carpet', 'headliner', 'door panels',
  'body', 'fenders', 'hood', 'trunk', 'quarter panels', 'doors', 'rocker',
  'electrical', 'wiring', 'harness', 'gauges', 'lights', 'fuse',
  'drivetrain', 'transmission', 'differential', 'driveshaft', 'clutch',
  'brakes', 'rotors', 'calipers', 'brake lines', 'master cylinder', 'pads', 'drums',
  'exhaust', 'headers', 'muffler', 'pipes',
  'cooling', 'radiator', 'water pump', 'thermostat', 'hoses',
  'fuel', 'gas tank', 'fuel lines', 'carburetor',
  'steering', 'steering wheel', 'steering column', 'rack',
  'wheels', 'tires', 'rims',
  'trim', 'chrome', 'molding', 'emblems',
  'glass', 'windshield', 'windows',
];

// Condition keywords for highlighting (green)
const CONDITION_KEYWORDS = [
  'as received', 'as-received', 'original', 'before',
  'disassembly', 'teardown', 'taking apart',
  'stripped', 'bare',
  'cleaned', 'degreased',
  'repaired', 'fixed', 'welded',
  'primed', 'primer',
  'painted', 'finished',
  'restored', 'after restoration',
  'installed', 'reassembled',
  'completed', 'done', 'final',
];

// Highlighted transcript component - shows recognized keywords
function HighlightedTranscript({ text }: { text: string }) {
  const lowerText = text.toLowerCase();
  const highlights: { start: number; end: number; type: 'component' | 'condition' }[] = [];

  // Find component keywords
  for (const keyword of COMPONENT_KEYWORDS) {
    let idx = lowerText.indexOf(keyword);
    while (idx !== -1) {
      highlights.push({ start: idx, end: idx + keyword.length, type: 'component' });
      idx = lowerText.indexOf(keyword, idx + 1);
    }
  }

  // Find condition keywords
  for (const keyword of CONDITION_KEYWORDS) {
    let idx = lowerText.indexOf(keyword);
    while (idx !== -1) {
      highlights.push({ start: idx, end: idx + keyword.length, type: 'condition' });
      idx = lowerText.indexOf(keyword, idx + 1);
    }
  }

  // Sort by start position and remove overlaps (keep longest match)
  highlights.sort((a, b) => a.start - b.start || b.end - a.end);
  const filtered: typeof highlights = [];
  for (const h of highlights) {
    if (filtered.length === 0 || h.start >= filtered[filtered.length - 1].end) {
      filtered.push(h);
    }
  }

  // Build JSX with highlighted spans
  if (filtered.length === 0) {
    return <>{text}</>;
  }

  const parts: React.ReactNode[] = [];
  let lastEnd = 0;

  for (const h of filtered) {
    if (h.start > lastEnd) {
      parts.push(text.slice(lastEnd, h.start));
    }
    parts.push(
      <span
        key={h.start}
        className={h.type === 'component' ? 'text-workshop-accent font-semibold' : 'text-emerald-400 font-semibold'}
      >
        {text.slice(h.start, h.end)}
      </span>
    );
    lastEnd = h.end;
  }

  if (lastEnd < text.length) {
    parts.push(text.slice(lastEnd));
  }

  return <>{parts}</>;
}

function WorkshopInterface() {
  const {
    mode,
    setMode,
    voiceState,
    setVoiceState,
    lastIntent,
    setLastIntent,
    transcript,
    setTranscript,
    currentVehicle,
    setCurrentVehicle,
    searchQuery,
    setSearchQuery,
    searchFilters,
    setSearchFilters,
    searchResults,
    setSearchResults,
    highlightedTags,
    setHighlightedTags,
    selectedImage,
    selectImage,
    comparisonPair,
    setComparisonPair,
    showcaseImages,
    setShowcaseImages,
    isLoading,
    setIsLoading,
    error,
    setError,
  } = useAppStore();

  const isKioskMode = import.meta.env.VITE_REQUIRE_PAIRING === 'true';

  // Vehicles lookup map for showcase mode (maps vehicleId -> Vehicle)
  const [vehiclesMap, setVehiclesMap] = useState<Map<string, Vehicle>>(new Map());

  // Pagination state
  const [hasMore, setHasMore] = useState(false);
  const [searchOffset, setSearchOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Workshop Memory state
  const [memoryMode, setMemoryMode] = useState(false);
  const [memoryQuery, setMemoryQuery] = useState<string | undefined>();
  const [memoryTimeRange, setMemoryTimeRange] = useState<{ relative?: string } | undefined>();
  const [memoryProofTarget, setMemoryProofTarget] = useState<string | undefined>();

  // Kiosk settings
  const {
    searchBarPosition,
    vehicleScope,
    setScopeToAll,
    setScopeToMake,
    setScopeToProject,
    voiceMode,
  } = useSettingsStore();

  // Voice recognition - Ventura wake word + push-to-talk + Web Speech API / Sherpa-ONNX
  const {
    state: voiceRecognitionState,
    transcript: voiceTranscript,
    isSupported: voiceSupported,
    startListening,
    stopListening,
    activatePushToTalk,
    isLoading: isVoiceLoading,
  } = useVenturaVoice({
    onIntent: handleVoiceIntent,
    onError: (err) => setError(err.message),
  });

  // Sync voice transcript to store for UI display
  useEffect(() => {
    if (voiceTranscript) {
      setTranscript(voiceTranscript);
    }
  }, [voiceTranscript, setTranscript]);

  // Handle mic button click - behavior depends on voice mode
  const handleMicClick = useCallback(async () => {
    console.log('Mic clicked, mode:', voiceMode, 'state:', voiceRecognitionState);

    if (voiceMode === 'ptt') {
      // In PTT mode: trigger push-to-talk (same as V key)
      console.log('PTT mode - activating push-to-talk');
      activatePushToTalk();
    } else {
      // In Full Voice mode: toggle listening on/off
      if (voiceRecognitionState === 'idle') {
        console.log('Starting listening...');
        await startListening();
      } else {
        console.log('Stopping listening...');
        await stopListening();
      }
    }
  }, [voiceMode, voiceRecognitionState, startListening, stopListening, activatePushToTalk]);

  // PTT hold-to-talk handlers
  const handleMicDown = useCallback(() => {
    console.log('Mic down - starting PTT');
    activatePushToTalk();
  }, [activatePushToTalk]);

  const handleMicUp = useCallback(() => {
    console.log('Mic up - stopping PTT');
    stopListening();
  }, [stopListening]);

  // Sync voice state to store
  useEffect(() => {
    setVoiceState(voiceRecognitionState);
  }, [voiceRecognitionState, setVoiceState]);

  // Load initial data on startup (works for both demo and real API)
  useEffect(() => {
    async function loadInitialData() {
      try {
        // Load vehicles
        const vehicles = await getVehicles();
        if (vehicles.length > 0 && !currentVehicle) {
          setCurrentVehicle(vehicles[0]);
        }
        setVehiclesMap(new Map(vehicles.map(v => [v.id, v])));

        // Load initial search results (all images) - scope will be applied
        const response = await searchImages({ query: '', scope: vehicleScope, limit: 20 });
        setSearchResults(response.results);
        setHasMore(response.hasMore);
        setTotalCount(response.totalCount);
        setSearchOffset(response.results.length);
      } catch (err) {
        console.error('Failed to load initial data:', err);
      }
    }

    loadInitialData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Track initial mount to avoid double-load
  const [hasMountedScope, setHasMountedScope] = useState(false);

  // Reload results when vehicle scope changes (from dropdown)
  useEffect(() => {
    // Skip initial mount - handled by loadInitialData
    if (!hasMountedScope) {
      setHasMountedScope(true);
      return;
    }

    // Reload search with new scope
    async function reloadForScope() {
      setIsLoading(true);
      try {
        const response = await searchImages({
          query: searchQuery || '',
          scope: vehicleScope,
          filters: searchFilters,
          limit: 20,
          offset: 0,
        });
        setSearchResults(response.results);
        setHasMore(response.hasMore);
        setTotalCount(response.totalCount);
        setSearchOffset(response.results.length);
      } catch (err) {
        console.error('Failed to reload for scope change:', err);
      } finally {
        setIsLoading(false);
      }
    }

    reloadForScope();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleScope]); // Only trigger on scope change

  // Idle timer for showcase mode (5 minutes)
  const { isIdle, reset: resetIdleTimer } = useIdleTimer({
    timeout: 5 * 60 * 1000,
    onIdle: () => {
      // Enter showcase when idle - scope determines which images to show
      if (mode === 'search') {
        enterShowcaseMode();
      }
    },
  });

  // Handle voice intent
  async function handleVoiceIntent(intent: VoiceIntent) {
    setLastIntent(intent);
    setTranscript(intent.rawTranscript);

    const command = intent.query?.toLowerCase() || intent.rawTranscript?.toLowerCase() || '';

    // PRIORITY: Handle comparison view commands when in comparison mode
    // These commands should work regardless of intent type
    if (mode === 'comparison' && command.length > 0) {
      let handled = false;

      // Switch to side-by-side
      if (command.includes('side by side') || command.includes('split view') || command.includes('split')) {
        window.dispatchEvent(new CustomEvent('voice-compare', { detail: { action: 'side-by-side' } }));
        handled = true;
      }
      // Switch to wipe slider
      else if (command.includes('wipe') || command.includes('slider') || command.includes('swipe') || command.includes('slide')) {
        window.dispatchEvent(new CustomEvent('voice-compare', { detail: { action: 'wipe' } }));
        handled = true;
      }
      // Start auto-sweep
      else if (command.includes('sweep') || command.includes('sweet') || command.includes('animate') ||
          command.includes('reveal') || command.includes('play') || command.includes('start') ||
          command.includes('demo') || command.includes('auto')) {
        window.dispatchEvent(new CustomEvent('voice-compare', { detail: { action: 'sweep-start' } }));
        handled = true;
      }
      // Stop sweep
      else if (command.includes('stop') || command.includes('pause') || command.includes('hold') || command.includes('freeze')) {
        window.dispatchEvent(new CustomEvent('voice-compare', { detail: { action: 'sweep-stop' } }));
        handled = true;
      }
      // Move slider left/before
      else if (command.includes('left') || command.includes('before') || command.includes('show before')) {
        window.dispatchEvent(new CustomEvent('voice-compare', { detail: { action: 'slider-left' } }));
        handled = true;
      }
      // Move slider right/after
      else if (command.includes('right') || command.includes('after') || command.includes('show after')) {
        window.dispatchEvent(new CustomEvent('voice-compare', { detail: { action: 'slider-right' } }));
        handled = true;
      }
      // Center slider
      else if (command.includes('center') || command.includes('middle') || command.includes('reset') || command.includes('fifty')) {
        window.dispatchEvent(new CustomEvent('voice-compare', { detail: { action: 'slider-center' } }));
        handled = true;
      }
      // Back/exit from comparison
      else if (command.includes('back') || command.includes('exit') || command.includes('close') || command.includes('done')) {
        setComparisonPair(null);
        handled = true;
      }

      // If we handled a comparison command, clear transcript and return
      if (handled) {
        setTimeout(() => setTranscript(''), 1500); // Clear after brief display
        return;
      }
    }

    // Handle back/exit commands in ANY mode (detail, showcase, memory)
    if (command.includes('back') || command.includes('exit') || command.includes('close')) {
      if (memoryMode) {
        setMemoryMode(false);
        setMemoryQuery(undefined);
        setMemoryTimeRange(undefined);
        setMemoryProofTarget(undefined);
        setTimeout(() => setTranscript(''), 1500);
        return;
      }
      if (mode === 'detail') {
        selectImage(null);
        setTimeout(() => setTranscript(''), 1500);
        return;
      }
      if (mode === 'showcase') {
        setMode('search');
        setTimeout(() => setTranscript(''), 1500);
        return;
      }
    }

    switch (intent.type) {
      case 'search':
        await executeSearch(intent);
        break;
      case 'compare':
        // Check if comparing by numbers
        if (intent.compareNumbers && searchResults.length > 0) {
          const [num1, num2] = intent.compareNumbers;
          const img1 = searchResults[num1 - 1]; // Convert to 0-indexed
          const img2 = searchResults[num2 - 1];
          if (img1 && img2) {
            setComparisonPair({
              beforeImage: img1,
              afterImage: img2,
              component: img1.component,
            });
          }
        } else {
          await executeComparison(intent);
        }
        break;
      case 'select':
        // Select image by number
        if (intent.imageNumber && searchResults.length > 0) {
          const idx = intent.imageNumber - 1; // Convert to 0-indexed
          if (idx >= 0 && idx < searchResults.length) {
            selectImage(searchResults[idx]);
          }
        }
        break;
      case 'similar':
        if (selectedImage) {
          await findSimilarImages(selectedImage.id);
        }
        break;
      case 'navigate':
        handleNavigation(intent);
        break;
      case 'scope':
        await handleScopeChange(intent);
        break;

      // Workshop Memory temporal queries
      case 'timeline':
        setMemoryQuery(intent.query);
        setMemoryTimeRange(undefined);
        setMemoryProofTarget(undefined);
        setMemoryMode(true);
        break;

      case 'period':
        setMemoryQuery(intent.query);
        setMemoryTimeRange(intent.timeRange);
        setMemoryProofTarget(undefined);
        setMemoryMode(true);
        break;

      case 'prove':
        setMemoryQuery(intent.query);
        setMemoryTimeRange(undefined);
        setMemoryProofTarget(intent.proofTarget);
        setMemoryMode(true);
        break;

      case 'when':
      case 'what-changed':
      case 'overlap':
        setMemoryQuery(intent.query);
        setMemoryTimeRange(intent.timeRange);
        setMemoryProofTarget(undefined);
        setMemoryMode(true);
        break;
    }
  }

  // Handle scope change voice commands
  async function handleScopeChange(intent: VoiceIntent) {
    if (!intent.scopeChange) return;

    const { type, make, projectName } = intent.scopeChange;

    if (type === 'all') {
      setScopeToAll();
      // Reload search results with new scope
      executeSearch(undefined, searchQuery || '');
      return;
    }

    if (type === 'make' && make) {
      setScopeToMake(make);
      executeSearch(undefined, searchQuery || '');
      return;
    }

    if (type === 'project' && projectName) {
      // Find the project by name
      try {
        const vehicles = await getVehicles();
        const matchingVehicle = vehicles.find(v =>
          v.name.toLowerCase().includes(projectName.toLowerCase())
        );
        if (matchingVehicle) {
          setScopeToProject(matchingVehicle.id, matchingVehicle.name, matchingVehicle.make, matchingVehicle.model);
          executeSearch(undefined, searchQuery || '');
        } else {
          setError(`No project found matching "${projectName}"`);
        }
      } catch (err) {
        setError('Could not change scope');
      }
    }
  }

  // Execute search from voice or touch
  async function executeSearch(intent?: VoiceIntent, directQuery?: string) {
    // Allow empty query to return all results within scope
    const query = directQuery !== undefined ? directQuery : (intent?.query || searchQuery || '');

    setIsLoading(true);
    setError(null);

    try {
      // Build filters from intent - update state so filter chips reflect the selection
      // Key behavior: Voice commands REPLACE filters, not ADD to them
      // - If user says "electrical", search all electrical (clear condition)
      // - If user says "electrical as received", search electrical + as-received
      // - Only keep existing filters if this is a direct query (not voice)
      const newFilters = intent ? {
        // Voice command: only apply what was explicitly mentioned
        component: intent.component,
        condition: intent.condition, // undefined if not mentioned = all states
      } : {
        // Direct query: keep existing filters
        ...searchFilters,
      };

      // Update filter chips UI to show active selections from voice
      if (intent) {
        setSearchFilters(newFilters);
      }

      // Include vehicle scope in search - reset pagination
      const response = await searchImages({
        query,
        scope: vehicleScope,
        filters: newFilters,
        limit: 20,
        offset: 0, // Always start fresh
      });

      setSearchResults(response.results);
      setHasMore(response.hasMore);
      setTotalCount(response.totalCount);
      setSearchOffset(response.results.length);

      // Highlight matching tags from the search (deduplicated)
      const tags = [
        intent?.component,
        intent?.condition,
        ...(query.split(' ').filter((w) => w.length > 3)),
      ].filter(Boolean) as string[];
      setHighlightedTags([...new Set(tags)]);

      resetIdleTimer();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setIsLoading(false);
    }
  }

  // Execute comparison
  async function executeComparison(intent: VoiceIntent) {
    if (selectedImage?.beforeImageId || selectedImage?.afterImageId) {
      setIsLoading(true);
      try {
        const beforeId = selectedImage.beforeImageId || selectedImage.id;
        const afterId = selectedImage.afterImageId || selectedImage.id;
        const pair = await getComparison(beforeId, afterId);
        setComparisonPair(pair);
      } catch (err) {
        setError('Could not load comparison');
      } finally {
        setIsLoading(false);
      }
    }
  }

  // Find similar images
  async function findSimilarImages(imageId: string) {
    setIsLoading(true);
    try {
      const similar = await getSimilarImages(imageId, 12);
      setSearchResults(similar);
      selectImage(null);
      setHighlightedTags(['similar']);
    } catch (err) {
      setError('Could not find similar images');
    } finally {
      setIsLoading(false);
    }
  }

  // Handle navigation commands (hands-free control)
  function handleNavigation(intent: VoiceIntent) {
    const command = intent.query?.toLowerCase() || '';

    // Next image
    if (command.includes('next') || command === 'forward') {
      if (mode === 'detail' && selectedImage && searchResults.length > 0) {
        const idx = searchResults.findIndex((r) => r.id === selectedImage.id);
        if (idx < searchResults.length - 1) {
          selectImage(searchResults[idx + 1]);
        }
      }
      return;
    }

    // Previous image
    if (command.includes('previous') || command.includes('prev') || command === 'back one') {
      if (mode === 'detail' && selectedImage && searchResults.length > 0) {
        const idx = searchResults.findIndex((r) => r.id === selectedImage.id);
        if (idx > 0) {
          selectImage(searchResults[idx - 1]);
        }
      }
      return;
    }

    // Zoom commands - dispatch custom events for ImageDetail to handle
    if (command.includes('zoom in') || command.includes('bigger') || command.includes('enlarge')) {
      window.dispatchEvent(new CustomEvent('voice-zoom', { detail: { direction: 'in' } }));
      return;
    }
    if (command.includes('zoom out') || command.includes('smaller')) {
      window.dispatchEvent(new CustomEvent('voice-zoom', { detail: { direction: 'out' } }));
      return;
    }

    // Comparison view commands
    if (mode === 'comparison') {
      // Switch view mode
      if (command.includes('side by side') || command.includes('split view') || command.includes('split')) {
        window.dispatchEvent(new CustomEvent('voice-compare', { detail: { action: 'side-by-side' } }));
        return;
      }
      if (command.includes('wipe') || command.includes('slider') || command.includes('swipe') || command.includes('slide')) {
        window.dispatchEvent(new CustomEvent('voice-compare', { detail: { action: 'wipe' } }));
        return;
      }
      // Sweep/animate the slider (many variations for speech recognition)
      if (command.includes('sweep') || command.includes('sweet') || command.includes('animate') ||
          command.includes('show me') || command.includes('reveal') || command.includes('play') ||
          command.includes('start') || command.includes('compare') || command.includes('demo')) {
        window.dispatchEvent(new CustomEvent('voice-compare', { detail: { action: 'sweep-start' } }));
        return;
      }
      // Stop sweep
      if (command.includes('stop') || command.includes('pause') || command.includes('hold') || command.includes('freeze')) {
        window.dispatchEvent(new CustomEvent('voice-compare', { detail: { action: 'sweep-stop' } }));
        return;
      }
      // Move slider manually
      if (command.includes('left') || command.includes('before') || command.includes('show before')) {
        window.dispatchEvent(new CustomEvent('voice-compare', { detail: { action: 'slider-left' } }));
        return;
      }
      if (command.includes('right') || command.includes('after') || command.includes('show after')) {
        window.dispatchEvent(new CustomEvent('voice-compare', { detail: { action: 'slider-right' } }));
        return;
      }
      if (command.includes('center') || command.includes('middle') || command.includes('reset') || command.includes('fifty')) {
        window.dispatchEvent(new CustomEvent('voice-compare', { detail: { action: 'slider-center' } }));
        return;
      }
    }

    // Close/back commands
    if (command.includes('back') || command.includes('exit') || command.includes('close')) {
      if (mode === 'detail') selectImage(null);
      else if (mode === 'comparison') setComparisonPair(null);
      else if (mode === 'showcase') setMode('search');
      return;
    }

    // Home command
    if (command.includes('home') || command.includes('main menu')) {
      setMode('search');
      selectImage(null);
      setComparisonPair(null);
      return;
    }

    // Showcase/screensaver command
    if (command.includes('showcase') || command.includes('screensaver') || command.includes('slideshow') || command.includes('show off')) {
      enterShowcaseMode();
    }
  }

  // Enter showcase mode - uses vehicle scope for filtering
  async function enterShowcaseMode() {
    try {
      // Use scope to determine which images to show
      const { heroImages } = await getShowcaseImages(
        vehicleScope.type === 'project' ? vehicleScope.projectId : undefined,
        vehicleScope
      );

      if (heroImages.length === 0) {
        setError('No showcase images found for current scope');
        return;
      }

      setShowcaseImages(heroImages);
      setMode('showcase');
    } catch (err) {
      console.error('Failed to enter showcase mode:', err);
      setError('Could not load showcase images');
    }
  }

  // Exit showcase mode
  const exitShowcaseMode = useCallback(() => {
    setMode('search');
    resetIdleTimer();
  }, [setMode, resetIdleTimer]);

  // Reset to home state - clear search, filters, and reload all images
  const handleReset = useCallback(async () => {
    setSearchQuery('');
    setSearchFilters({});
    setHighlightedTags([]);
    setTranscript('');
    selectImage(null);
    setComparisonPair(null);
    setMode('search');
    resetIdleTimer();

    // Reload all images within current scope - reset pagination
    try {
      setIsLoading(true);
      const response = await searchImages({ query: '', scope: vehicleScope, limit: 20, offset: 0 });
      setSearchResults(response.results);
      setHasMore(response.hasMore);
      setTotalCount(response.totalCount);
      setSearchOffset(response.results.length);
    } catch (err) {
      console.error('Failed to reset:', err);
    } finally {
      setIsLoading(false);
    }
  }, [
    setSearchQuery,
    setSearchFilters,
    setHighlightedTags,
    setTranscript,
    selectImage,
    setComparisonPair,
    setMode,
    resetIdleTimer,
    setIsLoading,
    setSearchResults,
    vehicleScope,
  ]);

  // Handle search submission
  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      executeSearch(undefined, query);
    },
    [setSearchQuery]
  );

  // Handle clearing all filters (for "All" button)
  const handleResetFilters = useCallback(
    async () => {
      const newFilters = {};
      setSearchFilters(newFilters);

      // Trigger search with cleared filters
      setIsLoading(true);
      try {
        const response = await searchImages({
          query: searchQuery || '',
          scope: vehicleScope,
          filters: newFilters,
          limit: 20,
          offset: 0,
        });
        setSearchResults(response.results);
        setHasMore(response.hasMore);
        setTotalCount(response.totalCount);
        setSearchOffset(response.results.length);
        setHighlightedTags([]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        setIsLoading(false);
      }
    },
    [setSearchFilters, searchQuery, vehicleScope, setIsLoading, setSearchResults, setHighlightedTags, setError]
  );

  // Handle filter changes - update filters and trigger search
  const handleComponentFilter = useCallback(
    async (component?: ComponentCategory) => {
      const newFilters = { ...searchFilters, component };
      setSearchFilters(newFilters);

      // Trigger search with new filters - reset pagination
      setIsLoading(true);
      try {
        const response = await searchImages({
          query: searchQuery || '',
          scope: vehicleScope,
          filters: newFilters,
          limit: 20,
          offset: 0,
        });
        setSearchResults(response.results);
        setHasMore(response.hasMore);
        setTotalCount(response.totalCount);
        setSearchOffset(response.results.length);

        // Update highlighted tags
        const tags = [component, newFilters.condition].filter(Boolean) as string[];
        setHighlightedTags([...new Set(tags)]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        setIsLoading(false);
      }
    },
    [searchFilters, setSearchFilters, searchQuery, vehicleScope, setIsLoading, setSearchResults, setHighlightedTags, setError]
  );

  const handleConditionFilter = useCallback(
    async (condition?: ConditionState) => {
      const newFilters = { ...searchFilters, condition };
      setSearchFilters(newFilters);

      // Trigger search with new filters - reset pagination
      setIsLoading(true);
      try {
        const response = await searchImages({
          query: searchQuery || '',
          scope: vehicleScope,
          filters: newFilters,
          limit: 20,
          offset: 0,
        });
        setSearchResults(response.results);
        setHasMore(response.hasMore);
        setTotalCount(response.totalCount);
        setSearchOffset(response.results.length);

        // Update highlighted tags
        const tags = [newFilters.component, condition].filter(Boolean) as string[];
        setHighlightedTags([...new Set(tags)]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        setIsLoading(false);
      }
    },
    [searchFilters, setSearchFilters, searchQuery, vehicleScope, setIsLoading, setSearchResults, setHighlightedTags, setError]
  );

  // Load more images for infinite scroll
  const handleLoadMore = useCallback(async () => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    try {
      const response = await searchImages({
        query: searchQuery || '',
        scope: vehicleScope,
        filters: searchFilters,
        limit: 20,
        offset: searchOffset,
      });

      // Append new results to existing
      setSearchResults([...searchResults, ...response.results]);
      setHasMore(response.hasMore);
      setSearchOffset(searchOffset + response.results.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more');
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, hasMore, searchQuery, vehicleScope, searchFilters, searchOffset, searchResults, setSearchResults, setError, setIsLoading]);

  // Keyboard shortcuts for navigation
  useKeyboardShortcuts({
    onEscape: () => {
      if (mode === 'detail') selectImage(null);
      else if (mode === 'comparison') setComparisonPair(null);
      else if (mode === 'showcase') setMode('search');
    },
    onNext: () => {
      if (mode === 'detail' && selectedImage && searchResults.length > 0) {
        const idx = searchResults.findIndex((r) => r.id === selectedImage.id);
        if (idx < searchResults.length - 1) selectImage(searchResults[idx + 1]);
      }
    },
    onPrevious: () => {
      if (mode === 'detail' && selectedImage && searchResults.length > 0) {
        const idx = searchResults.findIndex((r) => r.id === selectedImage.id);
        if (idx > 0) selectImage(searchResults[idx - 1]);
      }
    },
    onSelectNumber: (num) => {
      if (mode === 'search' && searchResults.length >= num) {
        selectImage(searchResults[num - 1]);
      }
    },
    onSearch: () => {
      // Focus search input
      const searchInput = document.querySelector('input[type="text"]') as HTMLInputElement;
      searchInput?.focus();
    },
    onShowcase: () => {
      if (mode === 'search') enterShowcaseMode();
      else if (mode === 'showcase') setMode('search');
    },
    onHome: handleReset,
    onVoiceActivate: () => {
      console.log('V key down - activating PTT');
      activatePushToTalk();
    },
    onVoiceDeactivate: () => {
      console.log('V key up - stopping PTT');
      stopListening();
    },
  });

  // Render based on mode
  return (
    <div className="min-h-screen bg-workshop-bg">
      {/* Showcase Mode - Full screen, no chrome */}
      <AnimatePresence>
        {mode === 'showcase' && showcaseImages.length > 0 && (
          <ShowcaseMode
            vehicles={vehiclesMap}
            heroImages={showcaseImages}
            onExit={exitShowcaseMode}
            onImageSelect={(image) => {
              setMode('detail');
              selectImage(image);
            }}
          />
        )}
      </AnimatePresence>

      {/* Comparison Mode */}
      <AnimatePresence>
        {mode === 'comparison' && comparisonPair && (
          <ComparisonView
            pair={comparisonPair}
            onClose={() => setComparisonPair(null)}
          />
        )}
      </AnimatePresence>

      {/* Workshop Memory Mode */}
      <AnimatePresence>
        {memoryMode && (
          <MemoryView
            vehicleId={vehicleScope?.projectId || 'v-001'}
            images={searchResults}
            initialQuery={memoryQuery}
            initialTimeRange={memoryTimeRange}
            initialProofTarget={memoryProofTarget}
            onImageClick={(imageId) => {
              const img = searchResults.find(i => i.id === imageId);
              if (img) selectImage(img);
            }}
            onClose={() => {
              setMemoryMode(false);
              setMemoryQuery(undefined);
              setMemoryTimeRange(undefined);
              setMemoryProofTarget(undefined);
            }}
          />
        )}
      </AnimatePresence>

      {/* Shared LayoutGroup for card→detail transitions */}
      <LayoutGroup>
        {/* Image Detail Mode */}
        <AnimatePresence mode="wait">
          {mode === 'detail' && selectedImage && (
            <ImageDetail
              image={selectedImage}
              onClose={() => selectImage(null)}
              onCompare={
                selectedImage.beforeImageId || selectedImage.afterImageId
                  ? () => executeComparison({ type: 'compare', rawTranscript: '', confidence: 1 })
                  : undefined
              }
              onShowSimilar={() => findSimilarImages(selectedImage.id)}
              onPrevious={
                searchResults.length > 0
                  ? () => {
                      const idx = searchResults.findIndex((r) => r.id === selectedImage.id);
                      if (idx > 0) selectImage(searchResults[idx - 1]);
                    }
                  : undefined
              }
              onNext={
                searchResults.length > 0
                  ? () => {
                      const idx = searchResults.findIndex((r) => r.id === selectedImage.id);
                      if (idx < searchResults.length - 1) selectImage(searchResults[idx + 1]);
                    }
                  : undefined
              }
              hasPrevious={
                searchResults.length > 0 &&
                searchResults.findIndex((r) => r.id === selectedImage.id) > 0
              }
              hasNext={
                searchResults.length > 0 &&
                searchResults.findIndex((r) => r.id === selectedImage.id) <
                  searchResults.length - 1
              }
            />
          )}
        </AnimatePresence>

        {/* Main Search Interface */}
        {mode === 'search' && (
        <div className={`flex min-h-screen ${searchBarPosition === 'bottom' ? 'flex-col-reverse' : 'flex-col'}`}>
          {/* Search Header - position controlled by kiosk settings */}
          <header className={`sticky z-30 bg-workshop-bg/95 backdrop-blur ${
            searchBarPosition === 'bottom'
              ? 'bottom-0 border-t border-workshop-border'
              : 'top-0 border-b border-workshop-border'
          }`}>
            <div className="max-w-7xl mx-auto px-6 py-4">
              {/* Header row with scope selector and controls */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <img
                    src="/vm-logo-icon.svg"
                    alt="Ventura Machina"
                    className="h-10 w-auto"
                  />
                  {DEMO_MODE && (
                    <span className="px-2 py-1 text-xs font-medium bg-workshop-accent/20 text-workshop-accent rounded">
                      DEMO
                    </span>
                  )}
                  {/* Vehicle Scope Selector - prominent position */}
                  <VehicleScopeSelector />
                </div>

                {/* Mic icon, home, screensaver, help, settings, and user button */}
                <div className="flex items-center gap-3">
                  {voiceSupported && (
                    <VoiceMicIcon
                      voiceState={voiceState}
                      isLoading={isVoiceLoading}
                      onClick={handleMicClick}
                    />
                  )}
                  <button
                    onClick={handleReset}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-workshop-surface border border-workshop-border text-workshop-text-muted hover:text-white hover:border-workshop-accent transition-all duration-200"
                    title="Reset to home (H)"
                  >
                    <HomeIcon className="w-5 h-5" />
                    <span className="text-sm hidden sm:inline">Home</span>
                  </button>
                  <button
                    onClick={enterShowcaseMode}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-workshop-surface border border-workshop-border text-workshop-text-muted hover:text-white hover:border-workshop-accent transition-all duration-200"
                    title="Start screensaver mode (F)"
                  >
                    <ScreensaverIcon className="w-5 h-5" />
                    <span className="text-sm hidden sm:inline">Screensaver</span>
                  </button>
                  <HelpButton />
                  <SettingsButton />
                  {!isKioskMode && <UserButton afterSignOutUrl="/" appearance={{ baseTheme: dark }} />}
                </div>

                {/* Error display */}
                {error && (
                  <div className="px-4 py-2 bg-workshop-error/20 border border-workshop-error rounded-lg text-workshop-error text-sm">
                    {error}
                  </div>
                )}
              </div>

              {/* Search bar - voice-first design */}
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                onSearch={handleSearch}
                voiceState={voiceState}
                onMicClick={handleMicClick}
                onMicDown={handleMicDown}
                onMicUp={handleMicUp}
                isPttMode={voiceMode === 'ptt'}
              />

              {/* Voice transcript display */}
              {voiceState === 'listening-command' ? (
                <div className="mt-3 px-4 py-3 bg-workshop-accent/10 border border-workshop-accent/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-workshop-accent animate-pulse" />
                    <span className="text-workshop-text-muted text-sm uppercase tracking-wider">
                      Hearing
                    </span>
                    <span className="text-white text-lg font-medium">
                      "<HighlightedTranscript text={transcript} />"
                    </span>
                  </div>
                </div>
              ) : (
                <div className="mt-3 px-4 py-2.5 rounded-xl border border-workshop-border/30">
                  <span className="text-workshop-text-muted text-sm">
                    Say "<span className="text-workshop-text-secondary">Hey Ventura</span>" or press <kbd className="px-1.5 py-0.5 rounded bg-workshop-border/20 text-workshop-text-secondary text-xs font-mono">V</kbd>
                  </span>
                </div>
              )}

              {/* Filter chips */}
              <div className="mt-4">
                <FilterChips
                  selectedComponent={searchFilters.component}
                  selectedCondition={searchFilters.condition}
                  onComponentChange={handleComponentFilter}
                  onConditionChange={handleConditionFilter}
                  onPhaseChange={() => {}}
                  onResetAll={handleResetFilters}
                />
              </div>
            </div>
          </header>

          {/* Results - Main content area */}
          <main className="flex-1 px-6 py-6 overflow-auto">
            <div className="max-w-7xl mx-auto">
              {/* Results count and info */}
              {searchResults.length > 0 && (
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-workshop-text-muted">
                    {searchResults.length} of {totalCount} results
                    {highlightedTags.length > 0 && (
                      <span className="ml-2">
                        for{' '}
                        {highlightedTags.map((tag, i) => (
                          <span key={tag}>
                            {i > 0 && ', '}
                            <span className="text-workshop-accent">{tag}</span>
                          </span>
                        ))}
                      </span>
                    )}
                  </p>
                </div>
              )}

              {/* Results grid */}
              <ResultsGrid
                images={searchResults}
                onImageClick={selectImage}
                onLoadMore={handleLoadMore}
                hasMore={hasMore}
                isLoading={isLoading}
                highlightedTags={highlightedTags}
              />

              {/* Empty state */}
              {searchResults.length === 0 && !isLoading && searchQuery === '' && (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-24 h-24 rounded-full bg-workshop-surface flex items-center justify-center mb-6">
                    <MicrophoneIcon className="w-12 h-12 text-workshop-accent" />
                  </div>
                  <h2 className="text-display-sm text-white mb-4">
                    Say "Hey Ventura"
                  </h2>
                  <p className="text-workshop text-workshop-text-muted max-w-md">
                    {DEMO_MODE
                      ? 'Try searching for "engine", "steering wheel", or "before and after". Demo data includes 6 classic cars with 40+ images.'
                      : 'Or type to search for images. Try "steering wheel as received" or "engine during teardown"'}
                  </p>
                </div>
              )}
            </div>
          </main>
        </div>
        )}
      </LayoutGroup>

      {/* Voice overlay - appears during active listening */}
      <VoiceOverlay
        state={voiceState}
        transcript={transcript}
        intent={lastIntent || undefined}
        onCancel={() => setVoiceState('listening-wake')}
        onConfirm={() => lastIntent && handleVoiceIntent(lastIntent)}
      />
    </div>
  );
}

function MicrophoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14z" />
    </svg>
  );
}

function ScreensaverIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
    </svg>
  );
}

function HomeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  );
}

function VoiceMicIcon({
  voiceState,
  isLoading,
  onClick,
}: {
  voiceState: string;
  isLoading: boolean;
  onClick: () => void;
}) {
  // Color based on voice state
  let dotColor = 'bg-gray-500'; // idle/off
  if (isLoading) {
    dotColor = 'bg-yellow-400';
  } else if (voiceState === 'listening-command') {
    dotColor = 'bg-blue-400';
  } else if (voiceState === 'listening-wake' || voiceState === 'processing') {
    dotColor = 'bg-emerald-400';
  }

  const isActive = voiceState === 'listening-command';

  return (
    <button
      onClick={onClick}
      className={clsx(
        'relative p-2 rounded-lg transition-all duration-200',
        'bg-workshop-surface border border-workshop-border',
        'text-workshop-text-muted hover:text-white hover:border-workshop-accent',
        isActive && 'border-blue-400/50 text-blue-400'
      )}
      title="Microphone"
    >
      <MicrophoneIcon className="w-5 h-5" />
      {/* Status dot */}
      <span className={clsx(
        'absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full border-2 border-workshop-bg',
        dotColor,
        isLoading && 'animate-pulse'
      )} />
    </button>
  );
}
